import { Action, AnyAction } from 'redux'
import { Epic, ofType } from 'redux-observable'
import {
  bufferTime,
  catchError,
  defaultIfEmpty,
  EMPTY,
  filter,
  from,
  identity,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  of,
  pipe,
  switchMap,
  takeUntil,
  timer
} from 'rxjs'
import { ErrorMessageContents, getHub } from '../helpers/hub'
import { logError } from '../store/ws/actions'
import { StreamDefinition } from './types'

type CancelOp = (
  action$: Observable<Action>,
  sourceAction: Action
) => MonoTypeOperatorFunction<AnyAction>

/*
  Optional operators resolve to `identity` so the chain stays a single typed
  .pipe() — spreading a variadic operator array breaks TS overload resolution.

  Using timer() before the hub call instead of delay() on the trigger stream,
  because a stream-level delay sits outside the switch/cancelOn scope — a
  superseded or cancelled trigger would still reach the hub once its delay
  elapsed. The timer is unsubscribed with the projection, so it never does.
*/
export const createStreamEpic = <TParams, TResponse>(
  triggerAction: string,
  /* `any` for TAction: the trigger-string-to-action-type link can't be checked here */
  definition: StreamDefinition<TParams, TResponse, any>
): Epic<Action> => {
  const flattenOp = definition.strategy === 'switch' ? switchMap : mergeMap
  const mapErrorToActions: (err: unknown, action: Action) => AnyAction[] =
    definition.mapError
      ? (err, action) =>
          [definition.mapError!(err as ErrorMessageContents, action)].flat()
      : definition.errorAction
      ? (err) => [{ type: definition.errorAction, payload: err, error: true }]
      : () => []
  const cancelOp: CancelOp =
    definition.cancelOn.length === 0
      ? () => identity
      : definition.cancelFilter
      ? (action$, sourceAction) =>
          takeUntil(
            action$.pipe(
              ofType(...definition.cancelOn),
              filter((ca: Action) => definition.cancelFilter!(ca, sourceAction))
            )
          )
      : (action$) => takeUntil(action$.pipe(ofType(...definition.cancelOn)))

  return (action$, state$) =>
    action$.pipe(
      ofType(triggerAction),
      flattenOp((action: Action) => {
        if (definition.shouldProcess && !definition.shouldProcess(action)) {
          return EMPTY
        }
        const params = definition.getParams(state$.value, action)
        const paramsArray = Array.isArray(params) ? params : [params]

        const callHub = () =>
          getHub().stream<TResponse>(definition.method, ...paramsArray)
        const source$ = definition.delayMs
          ? timer(definition.delayMs).pipe(mergeMap(callHub))
          : callHub()

        /*
          Per-item filter before buffering so filtered items never enter the
          batch. Pre-buffer emissions are single items even when TResponse is
          declared as the batch shape, hence the cast.
        */
        const filterOp: MonoTypeOperatorFunction<TResponse> =
          definition.filterResponse
            ? filter((r: TResponse) =>
                definition.filterResponse!(r as never, state$.value)
              )
            : identity

        /* buffered definitions declare TResponse as the flattened batch shape */
        const flatten: MonoTypeOperatorFunction<TResponse> = definition.bufferMs
          ? definition.emitEmptyBatch
            ? pipe(
                bufferTime<TResponse>(definition.bufferMs),
                filter((batch, index) => index === 0 || batch.length > 0),
                map((batch) => batch.flat() as unknown as TResponse)
              )
            : pipe(
                bufferTime<TResponse>(definition.bufferMs),
                filter((batch) => batch.length > 0),
                map((batch) => batch.flat() as unknown as TResponse)
              )
          : identity

        const emptyFallback: MonoTypeOperatorFunction<TResponse> =
          definition.bufferMs && definition.emitEmptyBatch
            ? defaultIfEmpty([] as unknown as TResponse)
            : identity

        return source$.pipe(
          filterOp,
          flatten,
          emptyFallback,
          mergeMap((response) => {
            const result = definition.mapResponse(
              response,
              action,
              state$.value
            )
            return Array.isArray(result) ? from(result) : of(result)
          }),
          cancelOp(action$, action),
          catchError((err: unknown) =>
            from([...mapErrorToActions(err, action), logError(err)])
          )
        )
      })
    )
}
